import React from 'react';
import { RatingScale } from '../../types/pms';
import { getRatingLabel } from '../../services/pmsRatingCalculator';
import './RatingDisplay.css';

interface RatingDisplayProps {
  rating?: RatingScale | number | '';
  /** Show as compact inline badge/card set */
  compact?: boolean;
  interactive?: boolean;
  onChange?: (rating: RatingScale) => void;
  label?: string;
}

const RATING_OPTIONS: { value: RatingScale; helper: string }[] = [
  { value: RatingScale.OUTSTANDING, helper: 'Exceptional impact and delivery' },
  { value: RatingScale.EXCEEDS_EXPECTATIONS, helper: 'Frequently exceeds expectations' },
  { value: RatingScale.MEETS_EXPECTATIONS, helper: 'Consistently delivers expected results' },
  { value: RatingScale.NEEDS_IMPROVEMENT, helper: 'Requires development in key areas' },
  { value: RatingScale.UNSATISFACTORY, helper: 'Performance below expectations' },
];

/**
 * RatingDisplay Component
 * Displays the official PMS 5-point rating scale.
 */
const RatingDisplay: React.FC<RatingDisplayProps> = ({
  rating,
  compact = false,
  interactive = false,
  onChange,
  label,
}) => {
  const numericRating = Number(rating || 0);
  const ratingValue = numericRating >= 1 && numericRating <= 5 ? numericRating as RatingScale : undefined;
  const displayLabel = ratingValue ? getRatingLabel(ratingValue) : 'Not rated';
  const starDisplay = ratingValue ? `${'★'.repeat(ratingValue)}${'☆'.repeat(5 - ratingValue)}` : '☆☆☆☆☆';

  const getRatingClass = (): string => {
    switch (ratingValue) {
      case RatingScale.OUTSTANDING: return 'rating-outstanding';
      case RatingScale.EXCEEDS_EXPECTATIONS: return 'rating-exceeds';
      case RatingScale.MEETS_EXPECTATIONS: return 'rating-meets';
      case RatingScale.NEEDS_IMPROVEMENT: return 'rating-needs';
      case RatingScale.UNSATISFACTORY: return 'rating-unsatisfactory';
      default: return '';
    }
  };

  if (interactive) {
    return (
      <div className={`pms-rating-picker ${compact ? 'pms-rating-picker--compact' : ''}`}>
        {label && <span className="pms-rating-picker__label">{label}</span>}
        <div className="pms-rating-picker__cards" role="radiogroup" aria-label={label || 'Performance rating'}>
          {RATING_OPTIONS.map(({ value, helper }) => (
            <button
              key={value}
              type="button"
              className={`pms-rating-picker__card ${numericRating === value ? 'pms-rating-picker__card--active' : ''}`}
              onClick={() => onChange?.(value)}
              role="radio"
              aria-checked={numericRating === value}
              aria-label={`${getRatingLabel(value)} (${value})`}
            >
              <span className="pms-rating-picker__card-icon" aria-hidden="true">★</span>
              <span className="pms-rating-picker__card-copy">
                <strong>{getRatingLabel(value)} ({value})</strong>
                <small>{helper}</small>
              </span>
            </button>
          ))}
        </div>
        <span className="pms-rating-picker__value">
          {ratingValue ? `${displayLabel} (${ratingValue}) selected` : 'Select a PMS rating category'}
        </span>
      </div>
    );
  }

  return (
    <span
      className={`pms-rating-display ${getRatingClass()} ${compact ? 'pms-rating-display--compact' : ''}`}
      aria-label={ratingValue ? `Rating: ${displayLabel} (${ratingValue})` : 'Rating not available'}
    >
      <span className="pms-rating-display__stars" aria-hidden="true">{starDisplay}</span>
      <span className="pms-rating-display__label">{displayLabel}</span>
    </span>
  );
};

export default RatingDisplay;
